import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ClipboardCheck, ChevronDown } from "lucide-react";

const slug = (label) => label.toLowerCase().replace(/\s+/g, "-");

export function NavBadge({ count, testid }) {
  if (!count) return null;
  return (
    <span data-testid={testid}
      className="ml-auto min-w-[1.375rem] h-[1.375rem] px-1.5 grid place-items-center rounded-full bg-[#D4AF37] text-[#0A2540] text-[11px] font-extrabold tabular-nums">
      {count > 99 ? "99+" : count}
    </span>
  );
}

export function SidebarSubItem({ to, label, icon: Icon, count }) {
  return (
    <NavLink to={to} data-testid={`admin-nav-${slug(label)}`}
      className={({ isActive }) =>
        `flex items-center gap-2.5 pl-7 pr-2.5 py-2 rounded-lg text-[13px] leading-tight font-semibold transition-colors ${
          isActive ? "bg-[#D4AF37] text-[#0A2540] shadow" : "text-slate-300 hover:bg-white/10 hover:text-white"
        }`}>
      <Icon size={15} />
      <span className="min-w-0 flex-1">{label}</span>
      <NavBadge count={count} testid={`admin-nav-badge-${slug(label)}`} />
    </NavLink>
  );
}

export function SidebarGroup({ label, icon: Icon = ClipboardCheck, items, counts, icons }) {
  const { pathname } = useLocation();
  const key = `advoset.adminNav.${slug(label)}.open`;
  const hasActiveChild = items.some((c) => pathname.startsWith(c.to));
  const [open, setOpen] = useState(() => {
    const saved = window.localStorage.getItem(key);
    return saved === null ? true : saved === "1";
  });
  useEffect(() => { if (hasActiveChild) setOpen(true); }, [hasActiveChild]);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    window.localStorage.setItem(key, next ? "1" : "0");
  };
  const total = items.reduce((n, c) => n + (counts?.[c.badge] || 0), 0);
  return (
    <div data-testid={`admin-nav-group-${slug(label)}`} data-state={open ? "open" : "closed"} className="pt-1">
      <button type="button" onClick={toggle} aria-expanded={open} aria-controls={`${slug(label)}-nav-items`}
        data-testid={`admin-nav-group-toggle-${slug(label)}`}
        className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-[11px] uppercase tracking-widest font-bold text-slate-400 hover:bg-white/10 hover:text-white transition-colors select-none">
        <Icon size={16} />
        <span className="flex-1 text-left">{label}</span>
        <NavBadge count={total} testid={`admin-nav-group-badge-${slug(label)}`} />
        <ChevronDown size={14} className={`shrink-0 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
      </button>
      <div id={`${slug(label)}-nav-items`} data-testid={`admin-nav-group-items-${slug(label)}`}
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className={`overflow-hidden ${open ? "" : "invisible"}`}>
          <div className="space-y-0.5 border-l border-white/10 ml-5 pt-0.5">
            {items.map((c) => (
              <SidebarSubItem key={c.to} to={c.to} label={c.label} icon={icons[c.icon] || Icon} count={counts?.[c.badge]} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
